import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Section } from "./Section";
import { PRICING } from "@/content/landing";

function priceLabel(tier, period) {
  if (tier.price === "Contact") return { amount: "Contact", unit: tier.unit };
  const amount = period === "annual" ? tier.priceAnnual : tier.priceMonthly;
  if (amount === 0) return { amount: "Free", unit: tier.unit };
  return { amount: `$${amount}`, unit: tier.unit };
}

export function Pricing() {
  const [period, setPeriod] = useState("monthly");

  return (
    <Section
      id="pricing"
      testId="landing-pricing"
      eyebrow={PRICING.eyebrow}
      title={PRICING.title}
      subtitle={PRICING.subtitle}
    >
      <div className="mb-8 flex justify-center">
        <div className="inline-flex items-center rounded-full border border-border/60 bg-card/60 p-1 font-mono text-[11px] uppercase tracking-[0.18em]">
          {[
            ["monthly", PRICING.toggle.monthly],
            ["annual", PRICING.toggle.annual],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`landing-pricing-toggle-${key}`}
              onClick={() => setPeriod(key)}
              className={`rounded-full px-4 py-1.5 transition-colors ${
                period === key
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-5">
        {PRICING.tiers.map((t, i) => {
          const { amount, unit } = priceLabel(t, period);
          return (
            <article
              key={t.name}
              data-testid={`landing-pricing-tier-${i}`}
              className={`relative flex flex-col rounded-2xl border p-6 transition-colors ${
                t.featured
                  ? "border-accent/60 bg-card shadow-[0_0_0_1px_hsl(var(--accent)/0.25)]"
                  : "border-border/60 bg-card/60 hover:border-foreground/30"
              }`}
            >
              {t.featured && (
                <span
                  data-testid="landing-pricing-featured-tag"
                  className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-accent-foreground"
                >
                  most popular
                </span>
              )}
              <div>
                <h3 className="font-mono text-sm uppercase tracking-[0.22em] text-muted-foreground">
                  {t.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="text-4xl font-semibold tracking-tight text-foreground">
                    {amount}
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    {unit}
                  </span>
                </div>
                {t.seats && (
                  <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-accent">
                    {t.seats}
                  </p>
                )}
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t.tagline}
                </p>
              </div>

              <ul className="my-6 space-y-2.5">
                {t.bullets.map((b, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2.5 text-[13px] leading-relaxed text-foreground/90"
                  >
                    <Check
                      className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-accent"
                      strokeWidth={2.2}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Link to={t.cta.href} className="mt-auto">
                <Button
                  data-testid={`landing-pricing-cta-${i}`}
                  size="lg"
                  variant={t.featured ? "default" : "outline"}
                  className={`w-full rounded-full font-mono text-xs uppercase tracking-[0.22em] ${
                    t.featured
                      ? "bg-accent text-accent-foreground hover:bg-accent/90"
                      : "border-border/80 text-foreground hover:bg-muted"
                  }`}
                >
                  {t.cta.label}
                </Button>
              </Link>
            </article>
          );
        })}
      </div>
    </Section>
  );
}
